import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { ThemeProvider } from 'styled-components';
import { themeState } from 'styleguide/theme';
import { H2, H5, P2 } from 'styleguide/Texts';
import { Card } from 'styleguide/Card';
import { View } from 'styleguide/View';
import { RoutineDetailViewModel } from './RoutineDetailViewModel';
import { SupersetGroupCell } from 'components/ExerciseSummary/SupersetGroupCell';
import { sendEvent } from 'utils/events';
import { stores } from 'state/stores';
import { Spacing } from 'styleguide/spacing';
import { RoutineOwnerContent } from './components/RoutineOwnerContent';
import { RoutineNotFoundView } from './components/RoutineNotFoundView';
import { RoutineDetailHevyCTACard } from './components/RoutineDetailHevyCTACard';
import { HideOnPhoneWrapper, ShowOnPhoneWrapper } from 'styleguide/Breakpoints';
import { HevyMenuItem, HevyOptionMenu } from 'components/HevyMenu';
import router from 'next/router';
import { FlexRow } from 'styleguide/Row';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { RoutineSummaryContent } from 'components/RoutineSummary/RoutineSummaryContent';
import { Icon } from 'styleguide/Icons';
import { ProfileImage } from 'components/ProfileImage';
import { t } from 'translations/t';

interface RoutineDetailScreenProps {
  routineId: string | null;
  /**
   * Needed for rendering the entire page correctly before the auth store is
   * initialised. There is probably a better way to do this, but for now this
   * will do.
   */
  isAuthenticated: boolean;
}

export const RoutineDetailScreen = ({ routineId, isAuthenticated }: RoutineDetailScreenProps) => {
  const [vm] = useState(new RoutineDetailViewModel({ routineId, isAuthenticated }));

  useEffect(() => {
    setTimeout(() => {
      /**
       * Without setTimeout this event is fired before
       * amplitude has been initialized.
       */
      sendEvent('routineDetail_screenview', {
        authenticated: stores.auth.isAuthenticated,
      });
    });
  }, []);

  return <RoutineDetailScreenInner vm={vm} />;
};

const ScreenContainer = styled(View)`
  flex: 1;
  overflow-y: auto;
  justify-content: center;
  align-items: center;
`;

const Container = styled(View)`
  flex: 1;
  width: 100%;
  max-width: 1048px;
  min-height: 90vh;
`;

const Content = styled(View)`
  flex-direction: row;
  justify-content: center;
  gap: ${Spacing.lg}px;
`;

const LeftContainer = styled(View)`
  flex: 684;
  @media (max-width: 820px) {
    min-width: 80vw;
  }
  max-width: 684px;
`;

const BackButton = styled(View)`
  padding-inline-end: ${Spacing.md}px;
  cursor: pointer;
`;

const ExercisesContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  padding: ${Spacing.md}px;
  margin-bottom: ${Spacing.md}px;
`;

const RightContainer = styled(HideOnPhoneWrapper)`
  display: flex;
  flex-direction: column;
  flex: 330;
  max-width: 330px;
  gap: ${Spacing.md}px;
`;

interface RoutineDetailScreenInnerProps {
  vm: RoutineDetailViewModel;
}

const RoutineDetailScreenInner = observer(({ vm }: RoutineDetailScreenInnerProps) => {
  if (vm.isInitialLoading) return <LoadingSpinner />;
  return (
    <ThemeProvider theme={themeState.current}>
      <ScreenContainer>
        <Container>
          {vm.isError ? (
            <RoutineNotFoundView />
          ) : (
            <View
              style={{
                paddingInlineStart: Spacing.lg,
                paddingInlineEnd: Spacing.lg,
                marginBottom: Spacing.lg,
                marginTop: Spacing.lg,
                gap: Spacing.md,
              }}
            >
              <FlexRow style={{ justifyContent: 'space-between' }}>
                <FlexRow>
                  {vm.isUsersRoutine && (
                    <BackButton onClick={() => router.back()}>
                      <Icon type="arrow-left" />
                    </BackButton>
                  )}
                  <H2>{vm.title}</H2>
                </FlexRow>
                {vm.isUsersRoutine && (
                  <HevyOptionMenu>
                    <HevyMenuItem
                      title={t('web.routineDetail.copyRoutineLink')}
                      onClick={vm.onCopyUrl}
                      iconType="copy"
                    />
                    <HevyMenuItem
                      title={t('web.routineDetail.editRoutine')}
                      onClick={() => router.push(`/edit-routine/${vm.routineId}`)}
                      iconType="edit"
                    />
                    <HevyMenuItem
                      title={t('web.routineDetail.deleteRoutine')}
                      onClick={vm.onDeleteRoutine}
                      iconType="trash"
                    />
                  </HevyOptionMenu>
                )}
              </FlexRow>
              <Content>
                <LeftContainer>
                  <ShowOnPhoneWrapper>
                    <FlexRow style={{ gap: Spacing.sm, padding: Spacing.md }}>
                      <ProfileImage source={vm.routineOwnerProfilePic} diameter={24} />
                      <P2 type="secondary">
                        {t('web.routineDetail.createdBy', { username: vm.username })}
                      </P2>
                    </FlexRow>
                  </ShowOnPhoneWrapper>

                  <ExercisesContainer>
                    {vm.supersetGroups.map((supersetGroup, index) => {
                      return (
                        <SupersetGroupCell
                          key={`${supersetGroup.supersetId || supersetGroup.exercises[0]?.title}`}
                          supersetId={supersetGroup.supersetId}
                          exercises={supersetGroup.exercises}
                          showDivider={index !== vm.supersetGroups.length - 1}
                        />
                      );
                    })}
                  </ExercisesContainer>
                  <ShowOnPhoneWrapper>
                    {!vm.isAuthenticated && <RoutineDetailHevyCTACard />}
                  </ShowOnPhoneWrapper>
                </LeftContainer>
                <RightContainer>
                  <Card style={{ padding: Spacing.lg }}>
                    <RoutineOwnerContent vm={vm} />
                  </Card>
                  {!vm.isAuthenticated && <RoutineDetailHevyCTACard />}
                  <Card style={{ padding: Spacing.lg, gap: Spacing.md }}>
                    <H5>{t('web.routineDetail.routineSummary')}</H5>
                    <RoutineSummaryContent
                      data={vm.heatmapData}
                      exerciseCount={vm.routineSummaryExerciseCount}
                      setCount={vm.routineSummarySetCount}
                      estimatedDuration={vm.routineSummaryEstimatedDuration}
                      setCountPerMuscleGroup={vm.setCountPerMuscleGroup}
                      heatmapHeight={170}
                      heatmapWidth={85}
                    />
                  </Card>
                </RightContainer>
              </Content>
            </View>
          )}
        </Container>
      </ScreenContainer>
    </ThemeProvider>
  );
});
